<template>
  <div
    :class="[
      joinClasses('select', modifiers),
      isEmptyList && 'select--disabled-color select--default-pointer',
      disabled && 'select--default-pointer'
    ]"
  >
    <div class="dropdown">
      <Popper
        v-model:open="open"
        :disabled="disabled || isEmptyList"
        :placement="placement"
      >
        <template #trigger>
          <div class="dropdown__trigger">
            <div class="select__trigger ">
              <div :class="labelStyle">
                <div
                  v-if="$slots.leadingIcon"
                  class="select__leading-icon"
                >
                  <slot name="leadingIcon" />
                </div>
                <div
                  v-if="icon"
                  class="select__label-icons"
                >
                  <div
                    :class="{
                      'select__label-icon': true,
                      'select__label-icon--placeholder': !modelValue,
                    }"
                  >
                    <UseSvg v-bind="icon" />
                  </div>
                </div>

                <span
                  v-if="!total"
                  :class="{
                    select__label: true,
                    'select__label--placeholder': !modelValue,
                    [labelClasses]: !!labelClasses,
                  }"
                  :style="labelLengthStyle"
                >
                  {{ label }}
                </span>
                <span
                  v-else
                  :class="{
                    select__label: true,
                    [labelClasses]: !!labelClasses,
                  }"
                >
                  {{ label }}
                  <span
                    v-if="showTotalInLabel"
                    :class="{
                      select__label: true,
                      'select__label--placeholder': true,
                    }"
                  >
                    {{ total }}
                  </span>
                </span>
              </div>
              <div
                v-if="!disabled || isEmptyList"
                :class="{ select__arrow: true, 'select__arrow--active': open }"
              >
                <UseSvg
                  class="icon icon--small"
                  name="arrow_down_16"
                />
              </div>
            </div>
          </div>
        </template>

        <div
          ref="dropdownContainerRef"
          class="menu menu--dropdown"
        >
          <div
            v-if="showCurrentSelection && modelValue"
            class="menu menu__show-selected"
          >
            <span>
              {{ items.find((item) => item.value === modelValue).label }}
            </span>
            <CircleCheckmark16Svg class="icon icon--small" />
          </div>
          <ul
            v-if="groups"
            class="menu__items"
            @mouseLeave="hoverItem(null)"
          >
            <template
              v-for="group in groups"
              :key="group.label"
            >
              <li :class="joinClasses('menu-item', ['group'])">
                <div class="menu-item__label">
                  {{ group.label }}
                </div>
              </li>
              <SelectItem
                v-for="item in group.items.filter((x) => !x.hidden)"
                :key="item.value"
                class="menu-item--indented"
                :iconsLargeOnRight="iconsLargeOnRight"
                :item="item"
                :labelIcon="showIconsInList ? labelIcons[item.value] : null"
                :selected="item.value === modelValue"
                @click="selectItem(item)"
                @mouse-enter="hoverItem(item)"
              />
            </template>
          </ul>
          <ul
            v-else
            class="menu__items"
            @mouseLeave="hoverItem(null)"
          >
            <SelectItem
              v-for="item in items.filter((x) => !x.hidden)"
              :key="item.value"
              :iconsLargeOnRight="iconsLargeOnRight"
              :item="item"
              :labelIcon="showIconsInList ? labelIcons[item.value] : null"
              :selected="item.value === modelValue"
              @click="selectItem(item)"
              @mouse-enter="hoverItem(item)"
            />
          </ul>
        </div>
      </Popper>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

import joinClasses from '@/functions/join_classes';

import Popper from '@/components/generic/popper.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import CircleCheckmark16Svg from '@/images/circle-checkmark-16.svg';
import SelectItem from '@/components/form/select_item.vue';

const props = defineProps({
  items: { type: Array, required: false, default: null },
  groups: { type: Array, required: false, default: null },
  labelIcons: { type: Object, default: null },
  placeholder: { type: String, default: '' },
  modelValue: { type: [Object, String, Number], default: null },
  placement: { type: String, default: 'bottom-end' },
  disabled: Boolean,
  showIconsInList: Boolean,
  labelClasses: { type: String, default: '' },
  modifiers: { type: Array, default: () => [] },
  showCurrentSelection: Boolean,
  iconsLargeOnRight: Boolean,
  collapse: Boolean,
  labelLengthLimit: { type: Number, required: false, default: null },
  labelStyle: {
    type: String,
    required: false,
    default: null,
    validator(value) {
      return ['pill'].includes(value);
    },
  },
  showTotalInLabel: { type: Boolean, required: false, default: true },
});
const emit = defineEmits(['update:modelValue', 'hover']);

const open = ref(false);
const dropdownContainerRef = ref();

const isEmptyList = computed(() => {
  return props.items?.length <= 0;
});

const icon = computed(() => {
  if (props.modelValue && props.labelIcons) {
    return props.labelIcons[props.modelValue];
  }
  return null;
});

const labelStyle = computed(() => {
  return {
    'select__label-wrapper': true,
    'select__label-wrapper--pill': props.labelStyle === 'pill',
  };
});

const label = computed(() => {
  let selected = findItem(
    (item) => item['value'] === props.modelValue
  );
  if (selected) return selected['label'];
  return props.placeholder;
});

const total = computed(() => {
  let selected = findItem(
    (item) => item['value'] === props.modelValue
  );
  return selected ? selected['total'] : null;
});

const labelLengthStyle = computed(() => {
  if (!props.labelLengthLimit) return null;
  return {
    width: `${props.labelLengthLimit}px`, textAlign: 'right'
  };
});

watch(() => props.collapse, () => open.value = !open.value );

function findItem(cb) {
  if (props.items) {
    return props.items.find(cb);
  } else if (props.groups) {
    for (let group of props.groups) {
      let found = group.items.find(cb);
      if (found) return found;
    }
  }
}

function selectItem(item) {
  emit('update:modelValue', item.value);
  open.value = false;
}

function hoverItem(item) {
  emit('hover', item?.value || null);
}

defineExpose({ dropdownContainerRef });
</script>

<style lang="scss" scoped>
@import '@/stylesheets/global';

.select__label-wrapper--pill {
  align-items: center;
  background-color: map-get($background-color, lightest);
  border-radius: map-get($border-radius, medium);
  border: 1px solid map-get($foreground-color, darker);
  display: flex;
  line-height: normal;
  padding: bu(0.5) bu(1);

  .select__label {
    margin: 0;
  }
}
</style>
